import { scaleFamilyLabels } from "@digital-twins/shared-types";

/**
 * Industry-standard 5-point scale wordings (RND-2675). English labels stay
 * the editor default; `ratingScaleTemplatesFor` overlays the study language.
 * Python AUTHOR/assembly read the same JSON (`config/questionnaire-scale-wording.json`).
 */
export const RATING_SCALE_TEMPLATES = {
  agreement: {
    label: "Agreement",
    labels: [
      "Strongly disagree",
      "Disagree",
      "Neither agree nor disagree",
      "Agree",
      "Strongly agree",
    ],
  },
  satisfaction: {
    label: "Satisfaction",
    labels: [
      "Very dissatisfied",
      "Dissatisfied",
      "Neither dissatisfied nor satisfied",
      "Satisfied",
      "Very satisfied",
    ],
  },
  likelihood: {
    label: "Likelihood",
    labels: ["Very unlikely", "Unlikely", "Neither likely nor unlikely", "Likely", "Very likely"],
  },
  concern: {
    label: "Level of concern",
    labels: [
      "Very unconcerned",
      "Unconcerned",
      "Neither concerned nor unconcerned",
      "Concerned",
      "Very concerned",
    ],
  },
  frequency: {
    label: "Frequency",
    labels: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  },
  awareness: {
    label: "Awareness",
    labels: [
      "Very unaware",
      "Unaware",
      "Neither aware nor unaware",
      "Aware",
      "Very aware",
    ],
  },
  familiarity: {
    label: "Familiarity",
    labels: [
      "Not at all familiar",
      "Slightly familiar",
      "Moderately familiar",
      "Very familiar",
      "Extremely familiar",
    ],
  },
  quality: {
    label: "Quality",
    labels: ["Very poor", "Poor", "Neither good nor poor", "Good", "Very good"],
  },
  importance: {
    label: "Importance",
    labels: [
      "Very unimportant",
      "Unimportant",
      "Neither important nor unimportant",
      "Important",
      "Very important",
    ],
  },
} as const;

export type RatingScaleTemplateKey = keyof typeof RATING_SCALE_TEMPLATES;

/** Template prefilled when a rating question is created from the frontend. */
export const DEFAULT_RATING_TEMPLATE_KEY: RatingScaleTemplateKey = "agreement";

/** A template as the editor shows it: the study language's labels, if any. */
export type RatingScaleTemplate = {
  readonly label: string;
  readonly labels: ReadonlyArray<string>;
};

export function ratingScaleTemplatesFor(
  language: string,
): Record<RatingScaleTemplateKey, RatingScaleTemplate> {
  const overlay = {} as Record<RatingScaleTemplateKey, RatingScaleTemplate>;
  (Object.keys(RATING_SCALE_TEMPLATES) as Array<RatingScaleTemplateKey>).forEach(
    (key) => {
      overlay[key] = {
        ...RATING_SCALE_TEMPLATES[key],
        labels:
          scaleFamilyLabels(key, language) ?? RATING_SCALE_TEMPLATES[key].labels,
      };
    },
  );
  return overlay;
}
